import { type Registry as Services, service } from '@ember/service';
import Model, { attr, type HasMany, hasMany } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { TotalRevenueSegmentationBreakdownReference } from 'lumen/models';
import {
  type Classification,
  CLASSIFICATION_TRANSLATIONS,
} from 'lumen/utils/constants/total-revenue-constants';
import safeDivide from 'lumen/utils/numeric/safe-divide';
import { segmentationValueFormatter } from 'lumen/utils/revenue-insight/segmentation-value-formatter';

export const SEGMENTATION_IDS = {
  SEGMENT: 'segment',
} as const;
export type SegmentationId =
  (typeof SEGMENTATION_IDS)[keyof typeof SEGMENTATION_IDS];

const UNMATCHED_ID = 'total_revenue.unmatched';

export default class TotalRevenueSegmentationBreakdownItem extends Model {
  [Type] = 'total-revenue/segmentation-breakdown/item' as const;

  @hasMany('total-revenue/segmentation-breakdown/reference', {
    async: false,
    inverse: 'totalRevenueItem',
  })
  declare referenceValues: HasMany<TotalRevenueSegmentationBreakdownReference>;

  @attr('string') declare classification: Classification | null;
  @attr('string') declare name: string;
  @attr('number') declare nonRoomPremium: number;
  @attr('number') declare nrOfRooms: number;
  @attr('string') declare segmentationId: SegmentationId;
  @attr('number') declare totalNonRoomRevenue: number;
  @attr('number') declare totalRevenue: number;
  @attr('number') declare totalRoomRevenue: number;

  @service declare intl: Services['intl'];
  @service('total-revenue')
  declare totalRevenueService: Services['total-revenue'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedName() {
    if (this.isUnmatched) {
      return this.intl.t('total_revenue.unmatched_total');
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return segmentationValueFormatter(this.segmentationId, this.name, {
      intl: this.intl,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenuePerRoomNight() {
    return this.totalRevenueService.formatValue(
      this.totalNonRoomRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenue() {
    return this.totalRevenueService.formatValue(this.totalRevenue);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenuePerRoomNight() {
    return this.totalRevenueService.formatValue(this.totalRevenuePerRoomNight);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenuePerRoomNight() {
    return this.totalRevenueService.formatValue(
      this.totalRoomRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isUnmatched() {
    return this.name === UNMATCHED_ID;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenuePerRoomNight() {
    return safeDivide(this.totalNonRoomRevenue, this.nrOfRooms);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenuePerRoomNight() {
    return safeDivide(this.totalRevenue, this.nrOfRooms);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenuePerRoomNight() {
    return safeDivide(this.totalRoomRevenue, this.nrOfRooms);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get translatedClassification() {
    if (!this.classification) {
      return this.intl.t('general.missing');
    }

    return this.intl.t(CLASSIFICATION_TRANSLATIONS[this.classification]);
  }
}
